<template>
  <div class="ds-root">
    <!-- Main Demo Session View -->
    <div v-if="curQuestion" class="ds-overlay">
      <!-- Top Navigation -->
      <div class="ds-header">
        <div class="ds-header-back" :style="{cursor: isProjector ? 'default' : 'pointer'}" @click="!isProjector && closeDemo()"><i v-if="!isProjector" class="el-icon-back"></i> {{ isReviewAnswerMode ? 'Review Session' : 'Demo Session' }}</div>
        <div class="ds-header-sub">Provide {{ isReviewAnswerMode ? 'review' : 'demo' }} for questions under specific bloom levels</div>
      </div>

      <!-- Scroll Wrapper -->
      <div class="ds-scroll-wrapper" ref="scrollContainer">
        <!-- Main Glass Card -->
        <div class="ds-main-card">
          <!-- Yellow Banner at TOP -->
          <div class="ds-yellow-banner" v-show="!isFullscreen && !isProjector">
            <div class="ds-banner-text">
              <h3>Explain the Solution</h3>
              <p>Speak while drawing to create a synchronized methodological breakdown.</p>
            </div>

            <div class="ds-banner-actions">
              <!-- Timer -->
              <div class="ds-timer" v-if="!isReviewAnswerMode && (isRecording || isRecordingStarting)">{{ formattedRecordingTime }}</div>

              <button class="ds-btn ds-btn-end" :disabled="isProcessingRecording" @click="handleEndDemo">
                {{ isReviewAnswerMode ? 'End Review' : 'End Demo' }}
              </button>
            </div>
          </div>

          <!-- Content Area (Question) -->
          <div class="ds-content-area" v-if="isCpaQuestion" style="display: flex; flex-direction: column; position: relative" v-show="!isFullscreen">
            <!-- Loading overlay when fetching level question -->
            <div
              v-if="loadingQuestion"
              style="
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(255, 255, 255, 0.7);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 10;
              ">
              <i class="el-icon-loading" style="font-size: 24px; color: #10b981"></i>
            </div>

            <!-- CPA Stats -->
            <div class="ds-cpa-stats" v-if="cpaStat">
              <span class="ds-stat-attempted">{{ cpaStat.attempt || 0 }} Attempted</span>
              <span class="ds-stat-stuck">{{ cpaStat.stuck || 0 }} Stuck</span>
              <span class="ds-stat-wrong">{{ cpaStat.wrong || 0 }} Wrong</span>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 24px">
              <div style="flex: 1; display: flex; flex-direction: column; gap: 12px">
                <!-- Expanded View -->
                <div v-show="isExpanded" class="ds-cpa-question" style="margin-bottom: 0" v-html="cpaQuestionHtml"></div>
                <!-- Collapsed View -->
                <div v-show="!isExpanded" class="ds-cpa-question ds-text-clamped" style="margin-bottom: 0" v-html="cpaQuestionHtml"></div>
              </div>

              <!-- CPA Slide Image Thumbnail -->
              <img
                v-if="curQuestion && curQuestion.pic"
                :src="Fn.hashToUrl(curQuestion.pic)"
                alt="Slide Image"
                style="max-height: 100px; max-width: 150px; border-radius: 8px; cursor: pointer; flex-shrink: 0; border: 1px solid #cbd5e1"
                @click="showSlidePopup = true" />

              <button class="ds-expand-btn" @click="isExpanded = !isExpanded" style="flex-shrink: 0">
                <i :class="isExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
              </button>
            </div>

            <!-- CPA Options -->
            <div v-show="isExpanded" class="ds-cpa-options">
              <div v-for="(option, i) in cpaCurrentOptions" :key="option._id || i" class="ds-option-item">
                <span class="ds-option-letter" :class="'letter-' + i">{{ String.fromCharCode(65 + i) }}</span>
                <span class="ds-option-text">{{ option.val || option.data || option.text || option }}</span>
              </div>
            </div>
          </div>

          <div
            class="ds-content-area ds-non-cpa-image-container"
            v-else
            style="display: flex; justify-content: center; padding: 16px; border-bottom: 1px solid #e2e8f0; background: #f8fafc"
            v-show="!isFullscreen">
            <img
              v-if="curQuestion && curQuestion.pic"
              :src="Fn.hashToUrl(curQuestion.pic)"
              alt="Slide Image"
              style="max-height: 150px; border-radius: 8px; cursor: pointer"
              @click="showSlidePopup = true" />
            <div
              v-else-if="curQuestion && (curQuestion.abstractData || (curQuestion.abstract && curQuestion.abstract.data))"
              style="padding: 16px; width: 100%; max-height: 150px; overflow-y: auto"
              v-html="curQuestion.abstractData || curQuestion.abstract.data"></div>
            <div v-else class="ds-no-image">No Image Available</div>
          </div>

          <!-- Drawing Canvas -->
          <div class="ds-canvas-area" ref="canvasArea">
            <button class="ds-fullscreen-btn" @click="toggleFullscreen">
              <i :class="isFullscreen ? 'el-icon-close' : 'el-icon-full-screen'"></i>
            </button>

            <iframe
              v-if="cpaDrawUrl"
              :src="cpaDrawUrl"
              frameborder="0"
              @load="handleIframeLoad"
              tabindex="-1"
              style="width: 100%; height: 100%; border: none"></iframe>
            <div v-else class="canvas-loading">
              <i class="el-icon-loading"></i>
              <span>LOADING WORKSHEET...</span>
            </div>

            <!-- Solve Here Watermark (behind iframe) -->
            <div class="ds-canvas-watermark">
              <i class="el-icon-edit"></i>
              <span>Solve Here</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- IMAGE PREVIEW OVERLAY -->
    <div v-if="showSlidePopup" class="gs-preview-overlay" @click.stop="showSlidePopup = false">
      <div class="gs-preview-close" @click.stop="showSlidePopup = false"><i class="el-icon-close"></i></div>
      <img v-if="curQuestion && curQuestion.pic" :src="Fn.hashToUrl(curQuestion.pic)" alt="Preview" class="gs-preview-image" @click.stop />
      <div v-else class="gs-preview-error">No image available</div>
    </div>

    <!-- RECORDING SAVED POPUP -->
    <div
      v-if="showRecordingSaved"
      class="gs-preview-overlay"
      style="z-index: 1000002; display: flex; justify-content: center; align-items: center; background: rgba(15, 23, 42, 0.3); backdrop-filter: blur(8px)"
      @click.stop>
      <div
        style="
          background: #ffffff;
          border: 1px solid rgba(226, 232, 240, 0.8);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
          padding: 32px;
          border-radius: 16px;
          text-align: center;
          color: #0f172a;
          width: 600px;
          max-width: 90vw;
          position: relative;
        ">
        <i
          class="el-icon-close"
          style="position: absolute; top: 24px; right: 24px; font-size: 20px; cursor: pointer; color: #94a3b8; transition: color 0.2s"
          onmouseover="this.style.color='#475569'"
          onmouseout="this.style.color='#94a3b8'"
          @click="cancelRecording"></i>
        <div style="text-align: left; margin-bottom: 24px">
          <div style="color: #0f172a; font-size: 18px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px">
            REVIEW YOUR RECORDING
          </div>
          <div style="color: #64748b; font-size: 14px; font-weight: 500">Preview your feedback before sending it to the student.</div>
        </div>
        <div
          style="
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            overflow: hidden;
            margin-bottom: 24px;
            box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
          ">
          <video :src="currentRecordingUrl" controls style="width: 100%; max-height: 300px; background: #0f172a; display: block; border-radius: 8px"></video>
        </div>
        <div style="border-top: 1px solid #e2e8f0; padding-top: 24px; display: flex; justify-content: space-between; gap: 16px">
          <button
            style="
              flex: 1;
              padding: 14px;
              background: #f1f5f9;
              border: 1px solid #e2e8f0;
              color: #475569;
              font-weight: 700;
              font-size: 13px;
              border-radius: 8px;
              cursor: pointer;
              transition: all 0.2s;
            "
            onmouseover="this.style.background='#e2e8f0'; this.style.color='#0f172a'; this.style.borderColor='#cbd5e1'"
            onmouseout="this.style.background='#f1f5f9'; this.style.color='#475569'; this.style.borderColor='#e2e8f0'"
            @click="cancelRecording">
            CANCEL
          </button>
          <button
            style="
              flex: 1;
              padding: 14px;
              border: none;
              font-weight: 700;
              font-size: 13px;
              border-radius: 8px;
              cursor: pointer;
              transition: all 0.2s;
              background: #00f0f0;
              color: #091a1d;
              box-shadow: 0 4px 12px rgba(0, 240, 240, 0.3);
            "
            onmouseover="this.style.background='#00d8d8'; this.style.boxShadow='0 6px 16px rgba(0, 216, 216, 0.4)'"
            onmouseout="this.style.background='#00f0f0'; this.style.boxShadow='0 4px 12px rgba(0, 240, 240, 0.3)'"
            @click="sendRecording">
            {{ ['group-log', 'analyticsDemo'].includes(recordingType) ? 'SEND TO STUDENTS' : 'SEND TO STUDENT' }}
          </button>
        </div>
      </div>
    </div>

    <!-- UPLOADING RECORDING OVERLAY -->
    <div
      v-if="isProcessingRecording"
      class="gs-preview-overlay"
      style="z-index: 1000005; display: flex; justify-content: center; align-items: center; background: rgba(0, 0, 0, 0.85)"
      @click.stop>
      <div style="text-align: center; color: #00ffff">
        <i class="el-icon-loading" style="font-size: 48px; margin-bottom: 24px; display: block"></i>
        <h2 style="margin: 0; margin-bottom: 12px; letter-spacing: 1px">{{ processingStep }}</h2>
        <p style="color: #8c99a0; font-size: 14px">Please do not close this window.</p>
      </div>
    </div>

    <!-- REASONING TAG POPUP -->
    <ReasoningTagDialog
      :visible="showReasoningTagDialog"
      :sid="computedSid"
      :student-id="computedStudentId"
      :question-id="computedQuestionId"
      :abstract-data="computedAbstractData"
      @saved="handleReasoningTagClosed"
      @close="handleReasoningTagClosed" />

    <!-- START DEMO POPUP -->
    <StartDemoPopup
      v-if="showStartDemoPopup"
      :type="recordingType"
      :curQuestion="activeQuestion || curQuestion"
      :cpaStat="demoPopupCpaStat"
      :isRedoDemo="computedIsRedoDemo"
      @start="confirmStartDemo"
      @close="handleClosePopup" />
  </div>
</template>

<script>
import {Room, VideoPresets} from 'livekit-client'
import StartDemoPopup from './StartDemoPopup.vue'
import ReasoningTagDialog from './ReasoningTagDialog.vue'

export default {
  name: 'StudentSlideViewPage',
  components: {
    StartDemoPopup,
    ReasoningTagDialog,
  },
  props: {
    curQuestion: {
      type: Object,
      required: true,
    },
    roomId: {
      type: String,
      default: '',
    },
    studentId: {
      type: String,
      default: '',
    },
    studentName: {
      type: String,
      default: '',
    },
    targetUids: {
      type: Array,
      default: () => [],
    },
    isRedCase: {
      type: Boolean,
      default: false,
    },
    isYellowCase: {
      type: Boolean,
      default: false,
    },
    hideRecordingBtn: {
      type: Boolean,
      default: false,
    },
    recordingType: {
      type: String,
      required: true,
    },
    isReviewAnswerMode: {
      type: Boolean,
      default: false,
    },
    isRedoDemo: {
      type: Boolean,
      default: false,
    },
    gameOnRecord: {
      type: Object,
      default: null,
    },
    cpaStatsList: {
      type: Array,
      default: () => [],
    },
    initialCpaStat: {
      type: Object,
      default: null,
    },
    isProjector: {
      type: Boolean,
      default: false,
    },
    initialDrawUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      dummyBloomLevels: [
        {num: 3, name: 'Apply'},
        {num: 4, name: 'Analyse'},
        {num: 5, name: 'Evaluate'},
        {num: 6, name: 'Create'},
      ],
      activeBloomIndex: 0,
      recordingSeconds: 0,
      formattedRecordingTime: '00:00',
      cpaBloomLevels: ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create'],
      cpaCurrentLevelIndex: 0,
      cpaDrawUrl: this.initialDrawUrl || '',
      activeCpaView: 'pictorial',
      showSlidePopup: false,
      isExpanded: true,
      isFullscreen: false,
      isMobile: false,
      isSupporting: false,
      activeAction: null,
      isRecording: false,
      isRecordingStarting: false,
      isProcessingRecording: false,
      processingStep: 'Processing the video...',
      showRecordingSaved: false,
      showStartDemoPopup: false,
      pendingShowStartDemoPopup: false,
      showReasoningTagDialog: false,
      currentRecordingId: null,
      currentRecordingUrl: '',
      recordingRoom: null,
      activeRecordingRoomName: null,
      recordingStartedListener: null,
      recordingStoppedListener: null,
      recordingErrorListener: null,
      activeQuestion: null,
      loadingQuestion: false,
    }
  },
  watch: {
    initialDrawUrl: {
      handler(val) {
        if (val) {
          this.cpaDrawUrl = val
        }
      },
      immediate: true,
    },
    curQuestion: {
      handler(val) {
        if (val) {
          this.activeQuestion = val
          const targetQId = (this.initialCpaStat && this.initialCpaStat.questionId) || val.questionId || val._id || val.id
          if (targetQId && this.bloomLevels && this.bloomLevels.length > 0) {
            const idx = this.bloomLevels.findIndex((l) => String(l.questionId) === String(targetQId))
            if (idx !== -1) {
              this.activeBloomIndex = idx
            }
          }
          this.$nextTick(() => {
            if (this.$el) {
              this.initDrawUrl()
              this.handleIframeLoad()
            }
          })
        }
      },
      deep: true,
      immediate: true,
    },
    initialCpaStat: {
      handler(stat) {
        if (stat && stat.questionId && this.bloomLevels && this.bloomLevels.length > 0) {
          const idx = this.bloomLevels.findIndex((l) => String(l.questionId) === String(stat.questionId))
          if (idx !== -1 && this.activeBloomIndex !== idx) {
            this.activeBloomIndex = idx
          }
        }
      },
      deep: true,
    },
    activeBloomIndex: {
      async handler(newIdx) {
        console.log('[DEBUG] activeBloomIndex watcher handler. newIdx:', newIdx)
        const levels = this.bloomLevels
        console.log('[DEBUG] activeBloomIndex watcher. levels:', levels)
        if (!levels || levels.length === 0) return

        const levelData = levels[newIdx]
        console.log('[DEBUG] activeBloomIndex watcher. levelData:', levelData)
        if (!levelData) return

        const curQId = this.curQuestion?._id || this.curQuestion?.id || this.curQuestion?.page
        if (String(levelData.questionId) === String(curQId)) {
          console.log('[DEBUG] levelData.questionId matches curQuestion ID')
          this.activeQuestion = this.curQuestion
          if (this.pendingShowStartDemoPopup) {
            this.showStartDemoPopup = true
            this.pendingShowStartDemoPopup = false
          }
          return
        }

        const expectedIdx = newIdx
        this.loadingQuestion = true
        try {
          if (window.App) {
            console.log('[DEBUG] Fetching subquestion from DB:', levelData.questionId)
            const fetched = await App.service('questions').get(levelData.questionId)
            if (fetched && this.activeBloomIndex === expectedIdx) {
              this.activeQuestion = fetched
              console.log('[DEBUG] Fetched question success:', fetched)
            }
          }
        } catch (err) {
          console.error('[DemoSessionPage] Failed to fetch level question:', err)
        } finally {
          if (this.activeBloomIndex === expectedIdx) {
            this.loadingQuestion = false
            if (this.pendingShowStartDemoPopup) {
              this.showStartDemoPopup = true
              this.pendingShowStartDemoPopup = false
            }
          }
        }
      },
      deep: true,
    },
  },
  computed: {
    computedSid() {
      return this.$store?.state?.session?.sid || this.roomId || ''
    },
    computedStudentId() {
      return this.studentId || (this.targetUids && this.targetUids[0]) || ''
    },
    computedQuestionId() {
      const q = this.activeQuestion || this.curQuestion
      return (q && (q._id || q.id)) || ''
    },
    computedAbstractData() {
      return this.cpaQuestionHtml || (this.curQuestion && (this.curQuestion.abstractData || this.curQuestion.title)) || ''
    },
    participantIdentity() {
      let uid = window.currentUserId || this.$route?.query?.uid
      if (!uid) {
        uid = sessionStorage.getItem('livekit_guest_uid')
        if (!uid) {
          uid = 'guest-' + Math.random().toString(36).substring(2, 11)
          sessionStorage.setItem('livekit_guest_uid', uid)
        }
      }
      return `${uid}_demo_${Math.random().toString(36).substring(2, 7)}`
    },
    Fn() {
      return window.Fn || {}
    },
    isGroupDemo() {
      return ['group-log', 'analyticsDemo'].includes(this.recordingType)
    },
    demoPopupCpaStat() {
      const activeLevel = this.bloomLevels[this.activeBloomIndex]
      const stat = this.cpaStat || {}
      return {
        levelNum: activeLevel ? activeLevel.num : this.cpaCurrentLevelIndex || 1,
        levelNameFormated: activeLevel ? activeLevel.name : 'Analyze',
        attempt: stat.attempt || '2%',
        correct: stat.correct || '10%',
        wrong: stat.wrong || '5%',
      }
    },
    computedIsRedoDemo() {
      const targetQId = this.activeQuestion?._id || this.curQuestion?._id || this.curQuestion?.id
      if (!targetQId) return false
      if (this.gameOnRecord && this.gameOnRecord.postGameDemos) {
        return this.gameOnRecord.postGameDemos.includes(targetQId)
      }
      return this.isRedoDemo
    },
    isCpaQuestion() {
      const isCpa = !!(this.curQuestion && (this.curQuestion.cpa?.outcome || this.curQuestion.isPostGameDemoCpa))
      console.log('isCpaQuestion is:', isCpa)
      return isCpa
    },
    cpaBloomLevel() {
      if (this.curQuestion && this.curQuestion.bloom && this.curQuestion.bloom.length > 0) {
        return this.curQuestion.bloom[0]
      }
      return this.cpaCurrentLevelIndex || 1
    },
    cpaBloomVerb() {
      if (this.curQuestion && this.curQuestion.bloom && this.curQuestion.bloom.length > 0) {
        return this.cpaBloomLevels[this.curQuestion.bloom[0] - 1] || 'Analyze'
      }
      return this.cpaBloomLevels[this.cpaCurrentLevelIndex] || 'Analyze'
    },
    cpaBloomPoints() {
      return this.curQuestion?.points || 2
    },
    cpaQuestionHtml() {
      const q = this.activeQuestion || this.curQuestion
      if (!q) return ''
      return q.abstractData || q.data || (q.abstract && q.abstract.data) || 'Question Data'
    },
    cpaCurrentOptions() {
      const q = this.activeQuestion || this.curQuestion
      if (!q) return []
      return q.options || []
    },
    bloomLevels() {
      if (!this.curQuestion) return []
      const BloomLabels = ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create']
      let questionsToProcess = []

      const bloomDict = this.curQuestion.bloom_questions || this.curQuestion.cpa?.bloom_questions
      const bloomArray = this.curQuestion.bloom || this.curQuestion.cpa?.bloom

      if (bloomDict && Object.keys(bloomDict).length > 0) {
        Object.entries(bloomDict).forEach(([key, qId]) => {
          const val = parseInt(key, 10)
          questionsToProcess.push({
            num: isNaN(val) ? 1 : val + 1,
            name: BloomLabels[val] || 'Analyze',
            questionId: qId,
          })
        })
      } else if (bloomArray && bloomArray.length > 0) {
        bloomArray.forEach((val) => {
          const numVal = parseInt(val, 10)
          questionsToProcess.push({
            num: numVal + 1,
            name: BloomLabels[numVal] || 'Analyze',
            questionId: this.curQuestion._id || this.curQuestion.id,
          })
        })
      } else {
        questionsToProcess.push({
          num: 4,
          name: 'Analyze',
          questionId: this.curQuestion._id || this.curQuestion.id,
        })
      }
      return questionsToProcess.sort((a, b) => a.num - b.num)
    },
    cpaStat() {
      if (this.cpaStatsList && this.cpaStatsList.length > 0) {
        const activeLevel = this.bloomLevels[this.activeBloomIndex]
        if (activeLevel) {
          const stat = this.cpaStatsList.find((s) => String(s.questionId) === String(activeLevel.questionId))
          if (stat) {
            return {attempt: stat.attempt, correct: stat.correct, wrong: stat.wrong}
          }
        }
      }
      if (this.curQuestion && this.curQuestion.cpaStat) {
        return this.curQuestion.cpaStat
      }
      return {attempt: 0, correct: 0, wrong: 0}
    },
  },
  async mounted() {
    document.body.appendChild(this.$el)
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    window.addEventListener('message', this.onDrawMessage)
    window.addEventListener('beforeunload', this.handleTabClose)

    if (window.App) {
      window.App.service('student-draw-key').on('patched', this.onDrawKeyPatched)
    }

    if (this.initialDrawUrl) {
      this.cpaDrawUrl = this.initialDrawUrl
    } else if (this.isRedCase) {
      await this.initDrawUrl()
    } else {
      await this.initDrawUrl()
    }
    this.handleIframeLoad()
    this.setupRecordingListeners()

    // Auto-start recording for all demo sessions, EXCEPT in Review Answer Mode or in Projector mode
    if (!this.isReviewAnswerMode && !this.isProjector) {
      this.handleRecording()
    }

    if (!this.isReviewAnswerMode && this.recordingType !== 'parellel-log' && window.EventBus && !this.isProjector) {
      window.EventBus.$emit('start-live-demo', {
        studentId: this.studentId,
        isGroupDemo: !!this.isGroupDemo,
        questionId:
          this.activeQuestion?._id ||
          this.activeQuestion?.id ||
          this.activeQuestion?.page ||
          this.curQuestion?.questionId ||
          this.curQuestion?._id ||
          this.curQuestion?.id ||
          this.curQuestion?.page ||
          '',
        cpaDrawUrl: this.cpaDrawUrl,
      })
    }
  },
  destroyed() {
    if (!this.isReviewAnswerMode && this.recordingType !== 'parellel-log' && window.EventBus && !this.isProjector) {
      window.EventBus.$emit('end-live-demo', {
        studentId: this.studentId,
        isGroupDemo: !!this.isGroupDemo,
      })
    }
    window.removeEventListener('resize', this.checkMobile)
    window.removeEventListener('message', this.onDrawMessage)
    window.removeEventListener('beforeunload', this.handleTabClose)
    if (window.App) {
      window.App.service('student-draw-key').removeListener('patched', this.onDrawKeyPatched)
    }
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
    this.stopTimer()
    this.cleanupRecording()
  },
  methods: {
    handleTabClose() {
      if (this.activeAction === 'recording' && this.activeRecordingRoomName) {
        const sid = this.$route.query.sid
        const roomName = this.activeRecordingRoomName
        const accessToken = window.App?.authentication?.accessToken
        if (roomName && accessToken) {
          const url = `${window.App.io.io.uri}/livekit-recording/${roomName}?sid=${sid}&type=${this.recordingType}`
          fetch(url, {
            method: 'DELETE',
            keepalive: true,
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }).catch((e) => console.error(e))
        }
      }
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
    },
    confirmStartDemo() {
      this.showStartDemoPopup = false
      this.handleRecording()
    },
    handleClosePopup() {
      this.showStartDemoPopup = false
    },
    handleBloomClick(idx) {
      if (this.isRecording || this.isProcessingRecording) return
      if (this.activeBloomIndex === idx) {
        if (!this.isReviewAnswerMode && this.isGroupDemo) {
          this.showStartDemoPopup = true
        }
        return
      }
      if (!this.isReviewAnswerMode) {
        if (this.isGroupDemo) {
          this.pendingShowStartDemoPopup = true
        } else {
          this.handleRecording()
        }
      }
      this.activeBloomIndex = idx
    },
    handleEndDemo() {
      if (this.isReviewAnswerMode) {
        this.closeDemo()
        return
      }
      if (this.isRecording) {
        this.stopRecording()
        // Wait for recording to process, do not close immediately
      } else {
        this.activeAction = null
        this.closeDemo()
      }
    },
    startTimer() {
      this.stopTimer()
      this.recordingSeconds = 0
      this.formattedRecordingTime = '00:00'
      this.timerInterval = setInterval(() => {
        this.recordingSeconds++
        const m = Math.floor(this.recordingSeconds / 60)
          .toString()
          .padStart(2, '0')
        const s = (this.recordingSeconds % 60).toString().padStart(2, '0')
        this.formattedRecordingTime = `${m}:${s}`
      }, 1000)
    },
    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    },
    handleRecording() {
      this.activeAction = 'recording'
      this.confirmRecording()
    },
    async confirmRecording() {
      if (this.isReviewAnswerMode) return
      this.showConfirmRecording = false
      if (this.isRecordingStarting || this.isRecording) return

      const sessionSid = this.$store?.state?.session?.sid || this.roomId
      const scopedRoom = `${sessionSid}__MainRoom`
      const callOpts = {timeout: 30000}

      this.isRecordingStarting = true
      try {
        const user = this.$store?.state?.user
        let baseUserName = user?.nickname || user?.name || 'Teacher'
        if (Array.isArray(baseUserName)) {
          baseUserName = baseUserName.join(' ')
        } else if (typeof baseUserName !== 'string') {
          baseUserName = String(baseUserName || 'Teacher')
        }
        let userName = `${baseUserName}_demo`
        if (!window.App) {
          throw new Error('Feathers client app not initialized on window.')
        }
        const response = await App.service('livekit').create({
          roomName: 'MainRoom',
          participantName: userName,
          participantIdentity: this.participantIdentity,
          sid: sessionSid,
        })
        const token = typeof response === 'string' ? response : response.token
        const actualRoom = response.roomName || scopedRoom
        this.activeRecordingRoomName = actualRoom

        // 2. Create a Room
        const room = new Room({
          adaptiveStream: true,
          dynacast: true,
          publishDefaults: {
            screenShareSimulcastLayers: [VideoPresets.h1080, VideoPresets.h720],
          },
        })

        const wsUrl = 'wss://dev-meeting.classcipe.com'
        await room.connect(wsUrl, token, {autoSubscribe: false})
        this.recordingRoom = room

        try {
          // Do not enable microphone on recordingRoom: the teacher's primary participant in EmbeddedMeetingSidebar
          // already handles meeting audio and microphone mute/unmute state. Enabling mic here would create a duplicate,
          // permanently unmuted audio track in MainRoom that bypasses mute controls.
          // Also set audio: false to prevent capturing and looping tab/meeting audio into MainRoom.
          await room.localParticipant.setScreenShareEnabled(true, {
            audio: false,
            selfBrowserSurface: 'include',
            preferCurrentTab: true,
          })
        } catch (shareErr) {
          console.warn('[REC] Screen share cancelled:', shareErr)
          this.isRecordingStarting = false
          this.activeAction = null
          await room.disconnect()
          this.recordingRoom = null
          return
        }

        this.isRecording = true
        this.isRecordingStarting = false
        this.startTimer()

        await App.service('livekit-recording').create({roomName: actualRoom, sid: sessionSid}, callOpts)
        console.log('[REC] Recording create API resolved successfully. Waiting for recording-started event.')
        this.$store.commit('teacher/setIsLiveDemoRecording', true)
      } catch (err) {
        this.isRecordingStarting = false
        this.activeAction = null
        console.error('[REC] Failed to start:', err)
        this.$message && this.$message.error(`Failed to start recording: ${err.message}`)
        if (this.recordingRoom) {
          try {
            await this.recordingRoom.disconnect()
          } catch (e) {}
          this.recordingRoom = null
        }
      }
    },
    async stopRecording() {
      if (this.isProcessingRecording) return
      this.stopTimer()

      const sessionSid = this.$store?.state?.session?.sid || this.roomId
      const scopedRoom = `${sessionSid}__MainRoom`
      const actualRoom = this.activeRecordingRoomName || scopedRoom
      const callOpts = {timeout: 30000}

      this.isProcessingRecording = true
      this.processingStep = 'Processing the video...'

      let success = false
      if (window.App) {
        try {
          await App.service('livekit-recording').remove(actualRoom, {
            ...callOpts,
            query: {sid: sessionSid},
          })
          success = true
        } catch (err) {
          console.error('[REC] Failed to stop recording egress:', err)
          this.$message && this.$message.error(`Failed to stop recording egress: ${err.message}`)
          this.isProcessingRecording = false
        }
      }

      if (success) {
        setTimeout(() => {
          if (this.isProcessingRecording) {
            this.isProcessingRecording = false
            this.isRecording = false
            this.$message && this.$message.warning('Recording stopped, but no file was received from the server.')
          }
        }, 15000)
      }

      try {
        if (this.recordingRoom) {
          await this.recordingRoom.localParticipant.setScreenShareEnabled(false)
          await this.recordingRoom.disconnect()
        }
      } catch (e) {
        console.warn('[REC] Cleanup error:', e)
      }
      this.recordingRoom = null
    },
    setupRecordingListeners() {
      if (!window.App || !App.service('livekit-recording')) return

      this.recordingStartedListener = (data) => {
        const sessionSid = this.$store?.state?.session?.sid || this.roomId
        const scopedRoom = `${sessionSid}__MainRoom`
        const actualRoom = this.activeRecordingRoomName || scopedRoom
        if (data.roomName !== actualRoom && data.roomName !== scopedRoom) return

        console.log('[REC] Received recording-started event for room:', data.roomName)
        this.isRecording = true
        this.isRecordingStarting = false
        if (!this.timerInterval) {
          this.startTimer()
        }
      }

      this.recordingStoppedListener = async (data) => {
        const sessionSid = this.$store?.state?.session?.sid || this.roomId
        const scopedRoom = `${sessionSid}__MainRoom`
        const actualRoom = this.activeRecordingRoomName || scopedRoom
        if (data.roomName !== actualRoom && data.roomName !== scopedRoom) return

        console.log('[REC] Stopped, file available at:', data.downloadUrl)
        this.isRecording = false
        this.stopTimer()

        if (data.downloadUrl) {
          try {
            this.isProcessingRecording = true

            const res = await fetch(data.downloadUrl)
            const blob = await res.blob()
            const file = new File([blob], data.fileName || `recording-${data.roomName}.mp4`, {type: 'video/mp4'})
            const uploadResult = await this.Fn.fileUpLoadUi(file)
            const sha1 = uploadResult.sha1 || uploadResult._id
            const r2Url = `https://r2${this.Fn.isDev ? 'dev' : ''}.classcipe.com/${sha1}`

            this.processingStep = 'Processing the video...'
            this.currentRecordingUrl = r2Url
            console.log('[REC] Uploaded to R2:', r2Url)
            if (window.App) {
              try {
                await App.service('livekit-recording').patch(null, {
                  action: 'delete-s3',
                  downloadUrl: data.downloadUrl,
                  roomName: data.roomName,
                  sid: sessionSid,
                })
                console.log('[REC] S3 file deleted successfully')
              } catch (delErr) {
                console.warn('[REC] Failed to delete original S3 file:', delErr)
              }
            }

            // 4. Save metadata with the new R2 URL
            if (window.App) {
              const teacherUid = this.$store?.state?.user?._id || ''
              const slideId =
                this.activeQuestion?._id ||
                this.activeQuestion?.id ||
                this.activeQuestion?.page ||
                this.curQuestion?.questionId ||
                this.curQuestion?._id ||
                this.curQuestion?.id ||
                this.curQuestion?.page ||
                ''
              const response = await App.service('review-recording').create({
                uid: teacherUid,
                sid: sessionSid,
                questionId: slideId,
                video: r2Url,
                type: this.recordingType,
              })
              console.log('DEBUG: review-recording created:', response)
              this.currentRecordingId = response._id
              this.$message && this.$message.success('Recording saved successfully!')
            }
          } catch (error) {
            console.error('[REC] Failed to process recording:', error)
            this.$message && this.$message.error('Failed to process and save recording')
          }
        } else {
          this.$message && this.$message.error('Recording stopped but no video URL was received.')
        }

        this.isProcessingRecording = false
        this.$store.commit('teacher/setIsLiveDemoRecording', false)
        if (this.recordingType === 'parellel-log') {
          this.showRecordingSaved = true
        } else {
          if (this.currentRecordingId) {
            this.sendRecording()
          }
        }
      }

      this.recordingErrorListener = (data) => {
        const sessionSid = this.$store?.state?.session?.sid || this.roomId
        const scopedRoom = `${sessionSid}__MainRoom`
        const actualRoom = this.activeRecordingRoomName || scopedRoom
        if (data.roomName !== actualRoom && data.roomName !== scopedRoom) return

        console.error('[REC] Recording error:', data.message)
        this.isRecording = false
        this.isRecordingStarting = false
        this.isProcessingRecording = false
        this.$store.commit('teacher/setIsLiveDemoRecording', false)
        this.$message && this.$message.error(`Recording Error: ${data.message || 'Failed to start/stop recording.'}`)
      }

      App.service('livekit-recording').on('recording-started', this.recordingStartedListener)
      App.service('livekit-recording').on('recording-stopped', this.recordingStoppedListener)
      App.service('livekit-recording').on('recording-error', this.recordingErrorListener)
    },
    cleanupRecording() {
      if (window.App && App.service('livekit-recording')) {
        if (this.recordingStartedListener) App.service('livekit-recording').removeListener('recording-started', this.recordingStartedListener)
        if (this.recordingStoppedListener) App.service('livekit-recording').removeListener('recording-stopped', this.recordingStoppedListener)
        if (this.recordingErrorListener) App.service('livekit-recording').removeListener('recording-error', this.recordingErrorListener)
      }
      if (this.recordingRoom) {
        try {
          this.recordingRoom.localParticipant.setScreenShareEnabled(false)
          this.recordingRoom.disconnect()
        } catch (e) {}
        this.recordingRoom = null
      }
    },
    async sendRecording() {
      this.showRecordingSaved = false
      this.activeAction = null

      if (window.App && this.currentRecordingId) {
        try {
          const sid =
            this.roomId ||
            this.curQuestion?.roomId ||
            this.gameOnRecord?.sessionId ||
            this.$store?.state?.session?.sid ||
            this.$store?.state?.class_id ||
            this.$route?.params?.id
          const gameRoomId = this.gameOnRecord?._id || this.curQuestion?.gameOnRecord?._id || this.curQuestion?.gameOnRecordId || this.roomId
          const isGroup = ['group-demo', 'group-log', 'analyticsDemo'].includes(this.recordingType)

          if (isGroup) {
            console.log('DEBUG: targetUids for group/analytics demo:', this.targetUids)
            if (this.targetUids && this.targetUids.length > 0) {
              await Promise.all(
                this.targetUids.filter(Boolean).map((uid) => {
                  console.log('DEBUG: creating game-on-feedback for uid:', uid)
                  return App.service('game-on-feedback').create({
                    uid: uid,
                    reviewRecordingId: this.currentRecordingId,
                  })
                })
              )
            }
          } else if (this.recordingType !== 'parellel-log' && this.targetUids && this.targetUids.length > 0) {
            console.log('DEBUG: targetUids for non-group demo (multiple targets):', this.targetUids)
            await Promise.all(
              this.targetUids.filter(Boolean).map((uid) => {
                console.log('DEBUG: creating game-on-feedback for uid:', uid)
                return App.service('game-on-feedback').create({
                  uid: uid,
                  reviewRecordingId: this.currentRecordingId,
                })
              })
            )
          } else {
            console.log('DEBUG: targetUids is empty! Falling back to single studentId:', this.studentId)
            await App.service('game-on-feedback').create({
              uid: this.studentId,
              reviewRecordingId: this.currentRecordingId,
            })
          }

          // 1. Primary real-time game-on notification
          if (gameRoomId && App.service('game-on')) {
            App.service('game-on')
              .get('sendRecordingNotification', {
                query: {
                  roomId: gameRoomId,
                  groupDemo: isGroup,
                  uids: isGroup || (this.targetUids && this.targetUids.length > 0) ? this.targetUids : [this.studentId],
                  uid: this.studentId,
                  reviewRecordingId: this.currentRecordingId,
                },
              })
              .catch((err) => console.warn('Failed to send game-on recording notification:', err))
          }

          // 2. Auth service fallback notification
          if (sid && App.service('auth')) {
            App.service('auth')
              .patch('syncTeacher', {
                commit: 'student/notifyRecordingReceived',
                _sid: sid,
                data: isGroup
                  ? {groupDemo: true, uids: this.targetUids}
                  : this.targetUids && this.targetUids.length > 0
                  ? {uids: this.targetUids}
                  : {uid: this.studentId},
              })
              .catch((err) => console.warn('Failed to send auth recording notification:', err))
          }

          let msg = 'Recording sent successfully!'
          if (['group-demo', 'group-log', 'analyticsDemo'].includes(this.recordingType)) {
            msg = 'Recorded video has been sent to all students.'
          } else {
            msg = `Recorded video has been sent to ${this.studentName || 'the student'}.`
          }
          this.$message && this.$message.success(msg)

          let emittedQuestion = this.activeQuestion || this.curQuestion
          if (this.bloomLevels && this.bloomLevels[this.activeBloomIndex]) {
            emittedQuestion = {
              ...emittedQuestion,
              _id: this.bloomLevels[this.activeBloomIndex].questionId,
            }
          }
          this.$emit('demo-sent', emittedQuestion)

          this.currentRecordingId = null
          this.currentRecordingUrl = ''

          if (!this.isReviewAnswerMode && this.recordingType !== 'analyticsDemo') {
            this.showReasoningTagDialog = true
          } else {
            this.closeDemo()
          }
        } catch (error) {
          console.error('Failed to send game-on-feedback:', error)
          this.$message && this.$message.error('Failed to send recording')
        }
      } else {
        if (window.App) {
          console.log('DEBUG: cancelling recording with type:', this.recordingType)
          const sid =
            this.roomId ||
            this.curQuestion?.roomId ||
            this.gameOnRecord?.sessionId ||
            this.$store?.state?.session?.sid ||
            this.$store?.state?.class_id ||
            this.$route?.params?.id
          const gameRoomId = this.gameOnRecord?._id || this.curQuestion?.gameOnRecord?._id || this.curQuestion?.gameOnRecordId || this.roomId
          const isGroup = ['group-demo', 'group-log', 'analyticsDemo'].includes(this.recordingType)

          if (gameRoomId && App.service('game-on')) {
            App.service('game-on')
              .get('cancelRecordingNotification', {
                query: {
                  roomId: gameRoomId,
                  groupDemo: isGroup,
                  uids: isGroup || (this.targetUids && this.targetUids.length > 0) ? this.targetUids : [this.studentId],
                  uid: this.studentId,
                },
              })
              .catch((err) => console.warn('Failed to cancel game-on recording notification:', err))
          }

          if (sid && App.service('auth')) {
            App.service('auth')
              .patch('syncTeacher', {
                commit: 'student/notifyRecordingCancelled',
                _sid: sid,
                data: isGroup
                  ? {groupDemo: true, uids: this.targetUids}
                  : this.targetUids && this.targetUids.length > 0
                  ? {uids: this.targetUids}
                  : {uid: this.studentId},
              })
              .catch((err) => console.warn('Failed to cancel auth recording notification:', err))
          }
        }
        let msg = 'Recording sent successfully!'
        if (['group-demo', 'group-log', 'analyticsDemo'].includes(this.recordingType)) {
          msg = 'Recorded video has been sent to all students.'
        } else {
          msg = `Recorded video has been sent to ${this.studentName || 'the student'}.`
        }
        this.$message && this.$message.success(msg)
      }
    },
    cancelRecording() {
      console.log('DEBUG: cancelRecording called with type:', this.recordingType)
      this.showRecordingSaved = false
      this.activeAction = null
      this.$store.commit('teacher/setIsLiveDemoRecording', false)
      this.recordingRoom = null
      this.currentRecordingId = null
      this.currentRecordingUrl = ''
    },
    handleJoin() {
      this.activeAction = 'join'
      if (window.App) {
        App.service('auth').patch('syncTeacher', {
          commit: 'student/notifyJoinTeacherRoom',
          data: {uid: this.studentId},
        })
      }
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    async initDrawUrl() {
      if (this.initialDrawUrl) {
        this.cpaDrawUrl = this.initialDrawUrl
        return
      }
      const sid = this.roomId
      const uid = this.studentId
      const slideId = this.activeQuestion?._id || this.curQuestion?._id || this.curQuestion?.id || this.curQuestion?.page
      if (this.curQuestion && sid && uid && slideId) {
        let studentDrawKey = ''
        try {
          if (window.App) {
            const findRes = await App.service('student-draw-key').find({
              query: {
                uid: uid,
                questionid: slideId,
                sid: sid,
              },
            })
            if (findRes && findRes.data && findRes.data.length > 0) {
              studentDrawKey = findRes.data[0].drawkey
            }
          }
        } catch (e) {
          console.warn('Failed to fetch student draw key', e)
        }

        if (studentDrawKey && this.recordingType !== 'parellel-log' && !this.isReviewAnswerMode) {
          const roomId = `${uid}_${slideId}_${sid}`
          this.cpaDrawUrl = `/draw/#room=${roomId},${studentDrawKey}`
        } else {
          if (this.isRedCase) {
            this.cpaDrawUrl = `/draw/`
          } else {
            const tempDrawkey = await this.Fn.drawKey()
            const roomSuffix = this.recordingType === 'parellel-log' || this.isReviewAnswerMode ? '_teacher' : ''
            this.cpaDrawUrl = `/draw/#room=view_${sid}_${this.studentId}${roomSuffix},${tempDrawkey}`
          }
        }
      } else if (this.curQuestion && sid) {
        if (this.isRedCase) {
          this.cpaDrawUrl = `/draw/`
        } else {
          const tempDrawkey = await this.Fn.drawKey()
          const roomSuffix = this.recordingType === 'parellel-log' || this.isReviewAnswerMode ? '_teacher' : ''
          this.cpaDrawUrl = `/draw/#room=view_${sid}_${this.studentId}${roomSuffix},${tempDrawkey}`
        }
      }
    },
    async onDrawMessage({data}) {
      if (data?.type === 'TRANSMART_CRX') return
      if (data?.app !== 'draw') return

      const uid = this.studentId
      const sid = this.roomId
      const slideId = this.activeQuestion?._id || this.curQuestion?._id || this.curQuestion?.id || this.curQuestion?.page

      if (!uid || !sid || !slideId) return

      if (data.ok) {
        try {
          const rs = await App.service('student-draw-key').find({query: {uid, questionid: slideId, sid}})
          const record = rs.data?.[0] || rs[0]

          if (record && (record.elements?.length > 0 || record.appState)) {
            this.currentDrawKeyDocId = record._id
            const drawIframe = this.$el.querySelector('iframe')
            if (drawIframe && drawIframe.contentWindow) {
              drawIframe.contentWindow.postMessage(
                {
                  app: 'draw',
                  elements: record.elements || [],
                  appState: record.appState || {viewBackgroundColor: 'transparent', pageId: slideId},
                },
                '*'
              )
            }
          }
        } catch (e) {
          console.error('[StudentSlideView] Failed to load drawing', e)
        }
      }

      if (data.type === 'change' && data.elements) {
        if (this.recordingType === 'parellel-log' || this.isReviewAnswerMode || this.isProjector) return
        if (!this.lastDrawSave) this.lastDrawSave = 0
        if (Date.now() - this.lastDrawSave > 1000) {
          this.lastDrawSave = Date.now()
          try {
            if (!this.currentDrawKeyDocId) {
              const rs = await App.service('student-draw-key').find({query: {uid, questionid: slideId, sid}})
              const record = rs.data?.[0] || rs[0]
              if (record) this.currentDrawKeyDocId = record._id
            }
            if (this.currentDrawKeyDocId) {
              App.service('student-draw-key').patch(this.currentDrawKeyDocId, {
                elements: data.elements,
                appState: data.appState,
                updatedByTeacher: true,
              })
            }
          } catch (e) {
            console.warn('Failed to save teacher stroke to DB', e)
          }
        }
      }
    },
    handleIframeLoad() {
      const container = this.$refs.scrollContainer
      if (!container) return

      const doReset = () => {
        if (container) {
          container.scrollTop = 0
        }
      }

      doReset()
      setTimeout(doReset, 50)
      setTimeout(doReset, 250)
      setTimeout(doReset, 600)
      setTimeout(doReset, 1200)
      setTimeout(doReset, 2500)
      setTimeout(doReset, 4000)
    },
    handleReasoningTagClosed() {
      this.showReasoningTagDialog = false
      this.closeDemo()
    },
    onDrawKeyPatched(record) {
      if (this.recordingType === 'parellel-log') return
      if (record.updatedByTeacher) return

      const uid = this.studentId
      const sid = this.roomId
      const slideId = this.activeQuestion?._id || this.curQuestion?._id || this.curQuestion?.id || this.curQuestion?.page

      if (record.uid === uid && record.sid === sid && record.questionid === slideId) {
        const drawIframe = this.$el.querySelector('iframe')
        if (drawIframe && drawIframe.contentWindow && record.elements) {
          drawIframe.contentWindow.postMessage(
            {
              app: 'draw',
              elements: record.elements,
            },
            '*'
          )
        }
      }
    },
    closeDemo() {
      if (this.activeAction === 'recording' && this.isRecording) {
        this.$message && this.$message.warning("Your Live Demo is still in progress. Please click 'End Demo' before returning to the classroom.")
        return
      }

      if (this.isSupporting && window.App) {
        App.service('auth').patch('syncTeacher', {
          commit: 'student/closeSlideSupport',
          data: {uid: this.studentId},
        })
      }

      // If closing before sending a recording, dismiss the 'Eyes up front' popup
      if (window.App) {
        if (this.recordingType === 'group-demo') {
          App.service('auth').patch('syncTeacher', {
            commit: 'student/notifyRecordingCancelled',
            data: {groupDemo: true, uids: this.targetUids},
          })
        } else if (this.targetUids && this.targetUids.length > 0) {
          App.service('auth').patch('syncTeacher', {
            commit: 'student/notifyRecordingCancelled',
            data: {uids: this.targetUids},
          })
        } else if (this.studentId) {
          App.service('auth').patch('syncTeacher', {
            commit: 'student/notifyRecordingCancelled',
            data: {uid: this.studentId},
          })
        }
      }

      this.$emit('close')
    },
    handleConfirmReview() {
      // Logic for confirm to be defined later
      console.log('Confirm clicked')
    },
    handleCpaSupport() {
      // Don't add support click functionality yet for CPA
      console.log('CPA Support button clicked - functionality to be added later')
    },
    handleSlideSupport() {
      this.isSupporting = true
      this.initDrawUrl()
      if (window.App) {
        App.service('auth').patch('syncTeacher', {
          commit: 'student/openSlideSupport',
          data: {
            uid: this.studentId,
            slide: this.curQuestion,
          },
        })
      }
    },
  },
}
</script>

<style scoped>
/* MAIN OVERLAY */
.cpadp-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #051416;
  z-index: 200000 !important;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #fff;
}

/* TOP HEADER */
.cpadp-header {
  height: 56px;
  background: #091315;
  border-bottom: 1px solid #1a3c40;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
}
.cpadp-title {
  color: #00ffff;
  font-weight: bold;
  font-size: 15px;
  letter-spacing: 1px;
}
.cpadp-close {
  color: #8c99a0;
  font-size: 24px;
  cursor: pointer;
  transition: 0.2s;
}
.cpadp-close:hover {
  color: #fff;
}

/* CONTENT STACK */
.cpadp-scroll-container {
  flex: 1;
  overflow-y: auto;
  padding: 2.5vh 5vw;
}
.cpadp-content-stack {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* CARDS */
.cpadp-card {
  background: #091315;
  border: 1px solid #1a3c40;
  border-radius: 12px;
  padding: 24px;
}

/* QUESTION CARD HEADER */
.q-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}
.q-header-left {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 32px;
  flex-wrap: wrap;
}
.bloom-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.bloom-line {
  display: flex;
  align-items: center;
  gap: 8px;
}
.bloom-label {
  color: #8c99a0;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
}
.bloom-verb {
  color: #fff;
  font-size: 15px;
  font-weight: 900;
  text-transform: uppercase;
}
.points-badge {
  color: #00ffff;
  font-size: 11px;
  font-weight: 800;
}

.q-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.support-btn {
  background: transparent;
  border: 1px solid #ff00ff;
  border-radius: 6px;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 10px 18px;
  cursor: pointer;
  letter-spacing: 1px;
  transition: 0.2s;
}
.support-btn:hover {
  background: rgba(255, 0, 255, 0.1);
}

.question-count-badge {
  background: #1c2135;
  color: #8c99a0;
  font-size: 11px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 6px;
  letter-spacing: 0.5px;
}

/* QUESTION CONTENT BOX */
.q-content-box {
  position: relative;
  background: rgba(9, 26, 30, 0.35);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(0, 255, 255, 0.12);
  border-left: 5px solid #00ffff;
  border-radius: 12px;
  padding: 20px 24px;
  transition: all 0.3s ease;
}
.q-expand-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  background: rgba(0, 255, 255, 0.15);
  color: #00ffff;
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
}
.q-expand-btn:hover {
  background: rgba(0, 255, 255, 0.25);
}

/* QUESTION CONTENT */
.q-text {
  font-size: 16px;
  line-height: 1.7;
  color: #e8f4f6;
  margin-bottom: 24px;
  padding-right: 32px;
}
.q-text-clamped {
  font-size: 16px;
  color: #e8f4f6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 40px;
}
.options-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}
.option-item {
  font-size: 16px;
  display: flex;
  gap: 8px;
  color: #ccd6d9;
}
.option-letter {
  color: #8c99a0;
  font-weight: bold;
}

.multi-notice {
  background: rgba(0, 255, 255, 0.05);
  border-radius: 6px;
  padding: 10px 16px;
  color: #8c99a0;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.multi-notice span {
  color: #00ffff;
  font-weight: bold;
}

/* NORMAL SLIDE VIEW */
.ns-thumbnail-wrap {
  width: 240px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  border: 1px solid #1a3c40;
  display: flex;
  flex-direction: column;
}
.ns-thumbnail-wrap img {
  width: 100%;
  display: block;
  filter: brightness(0.85);
}
.ns-actions {
  display: flex;
  background: #0d2729;
  border-top: 1px solid #1a3c40;
}
.ns-action-btn {
  flex: 1;
  padding: 8px;
  color: #00ffff;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-right: 1px solid #1a3c40;
  transition: background-color 0.2s;
}
.ns-action-btn:last-child {
  border-right: none;
}
.ns-action-btn:hover {
  background: #113133;
}

.q-title-only {
  font-size: 16px;
  font-weight: bold;
  color: #00ffff;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* WORKSHEET HEADER */
.cpadp-worksheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}
.ws-main {
  font-size: 18px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ws-main i {
  color: #00ffff;
}
.ws-sub {
  font-size: 10px;
  color: #4a6870;
  font-weight: bold;
  letter-spacing: 1px;
  margin-top: 4px;
}

/* CANVAS */
.cpadp-canvas-container {
  height: 85vh;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #1a3c40;
  position: relative;
}
.cpadp-canvas-container iframe {
  width: 100%;
  height: 100%;
  border: none;
}
.canvas-loading {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #091315;
  color: #8c99a0;
}

/* PREVIEW OVERLAY (Custom Popup) */
.gs-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000001; /* Higher than cpadp-overlay */
}

.gs-preview-image {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 8px;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.8);
}

.gs-preview-close {
  position: absolute;
  top: 30px;
  right: 30px;
  color: #fff;
  font-size: 32px;
  cursor: pointer;
  z-index: 10;
  background: rgba(0, 0, 0, 0.5);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
}
.gs-preview-close:hover {
  background: #ff0000;
  transform: rotate(90deg);
}

.gs-preview-error {
  color: #8c99a0;
  font-size: 18px;
  font-weight: bold;
}

/* CPA SPLIT LAYOUT */
.cpa-split-layout {
  display: flex;
  gap: 24px;
  width: 100%;
}
.cpa-split-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #091315;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #1a3c40;
}
.cpa-split-header {
  background: #0d2729;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #1a3c40;
}
.cpa-split-title {
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 1px;
  color: #00ffff;
  text-transform: uppercase;
}
.cpa-view-toggle {
  display: flex;
  background: rgba(9, 26, 30, 0.35);
  border: 1px solid #1a3c40;
  border-radius: 6px;
  overflow: hidden;
}
.cpa-view-toggle button {
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  border: none;
  background: transparent;
  color: #8c99a0;
  cursor: pointer;
  transition: 0.2s;
}
.cpa-view-toggle button.active {
  background: rgba(0, 255, 255, 0.15);
  color: #00ffff;
}
.cpa-split-body {
  flex: 1;
  position: relative;
  background: rgba(9, 26, 30, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  overflow: hidden;
}
.cpa-split-grid-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: 40px 40px;
  background-image: linear-gradient(to right, rgba(26, 60, 64, 0.5) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(26, 60, 64, 0.5) 1px, transparent 1px);
  opacity: 0.5;
  z-index: 1;
}
.cpa-split-image {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  z-index: 2;
  position: relative;
}
.cpa-no-image {
  color: #8c99a0;
  font-size: 14px;
  z-index: 2;
  position: relative;
}
.cpa-split-canvas.cpadp-canvas-container {
  flex: 1;
  border: none;
  border-radius: 0;
  height: auto;
  min-height: 600px;
  background: #fff;
}

/* MOBILE OPTIMIZATION */
@media (max-width: 768px) {
  .cpadp-scroll-container {
    padding: 16px 16px 80px 16px;
  }
  .cpadp-content-stack {
    gap: 16px;
  }
  .q-card-header {
    margin-bottom: 16px;
  }
  .bloom-line {
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .bloom-label {
    font-size: 8px;
    flex-shrink: 0;
  }
  .bloom-verb {
    font-size: 12px;
  }
  .points-badge {
    font-size: 8px;
    white-space: nowrap;
  }

  .q-header-mobile {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    gap: 8px;
  }
  .q-header-right-mobile {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: -2px;
    flex-shrink: 0;
  }

  .q-text,
  .q-text-clamped {
    font-size: 14px;
  }
  .option-item {
    font-size: 14px;
  }

  .multi-notice {
    font-size: 11px;
    padding: 8px 12px;
    gap: 6px;
  }
  .multi-notice span {
    font-size: 11px;
  }

  .cpadp-canvas-container {
    height: 70vh;
  }
}

/* DEMO SESSION STYLES */
.ds-root {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000000;
  font-family: 'Inter', sans-serif;
}
.ds-overlay {
  position: absolute;
  inset: 0;
  z-index: 100000;
  background-image: url('https://r2.classcipe.com/5ba969ff64d08ea536f0b56648c9f7be2d8343db');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  padding: 16px 0 0 0;
  box-sizing: border-box;
  overflow: hidden;
}

/* Header */
.ds-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 4px 20px 25px;
  color: white;
  gap: 8px;
}
.ds-header-back {
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s;
}
.ds-header-back:hover {
  opacity: 0.8;
}
.ds-header-sub {
  color: #ffffff;
  font-size: 14px;
  opacity: 0.9;
}

/* Bloom Pills */
.ds-bloom-pills {
  display: flex;
  gap: 12px;
  margin: 0 10vw 24px;
  padding-bottom: 8px;
}
.ds-bloom-pill {
  padding: 8px 20px;
  background: rgba(255, 255, 255, 0.4);
  border: 2px solid #ffffff;
  border-radius: 20px;
  color: #1e1b4b;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  backdrop-filter: blur(6px);
}
.ds-bloom-pill:hover:not(.disabled) {
  background: rgba(255, 255, 255, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.ds-bloom-pill.active {
  background: #7c3aed;
  border-color: #fde047;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
}
.ds-bloom-pill.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Scroll Wrapper */
.ds-scroll-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  padding: 0 20px 25px;
  box-sizing: border-box;
}

/* Main Card */
.ds-main-card {
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.2);
}
.ds-scroll-wrapper::-webkit-scrollbar {
  width: 8px;
}
.ds-scroll-wrapper::-webkit-scrollbar-track {
  background: transparent;
}
.ds-scroll-wrapper::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
}
.ds-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.ds-canvas-area {
  flex: 1;
  min-height: 800px;
  background: #f8fafc;
  background-image: radial-gradient(#cbd5e1 1px, transparent 1px);
  background-size: 20px 20px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
}

.ds-fullscreen-btn {
  position: absolute;
  top: 16px;
  right: 64px;
  width: 44px;
  height: 44px;
  background: white;
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s;
}
.ds-fullscreen-btn i {
  font-size: 20px;
  color: #7c3aed;
  font-weight: bold;
}
.ds-fullscreen-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

/* Yellow Banner */
.ds-yellow-banner {
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 16px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.ds-banner-text h3 {
  margin: 0 0 4px 0;
  color: #92400e;
  font-size: 18px;
  font-weight: 800;
}
.ds-banner-text p {
  margin: 0;
  color: #b45309;
  font-size: 12px;
}
.ds-banner-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}
.ds-timer {
  color: #b91c1c;
  font-weight: bold;
  font-size: 18px;
  font-family: monospace;
}
.ds-btn {
  padding: 10px 24px;
  border-radius: 20px;
  font-weight: bold;
  cursor: pointer;
  border: none;
  font-size: 14px;
  transition: opacity 0.2s;
}
.ds-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
.ds-btn:hover {
  opacity: 0.9;
}
.ds-btn-recording {
  background: #ef4444;
  color: white;
}
.ds-btn-recording-stop {
  background: #10b981;
  color: white;
}
.ds-btn-end {
  background: #4f46e5;
  color: white;
}

/* Content Area */
.ds-content-area {
  padding: 24px 32px;
  background: white;
  border-radius: 12px;
}
.ds-cpa-stats {
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 20px;
  padding: 4px 10px;
  gap: 12px;
  margin-bottom: 16px;
  align-self: flex-start;
}
.ds-cpa-stats span {
  font-size: 12px !important;
  font-weight: 600;
}
.ds-stat-attempted {
  color: #64748b;
}
.ds-stat-stuck {
  color: #eab308;
}
.ds-stat-wrong {
  color: #ef4444;
}

.ds-cpa-question {
  font-size: 18px;
  color: #1e293b;
  font-weight: bold;
  margin-bottom: 24px;
}
.ds-cpa-options {
  display: flex;
  gap: 24px;
}
.ds-option-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  color: #334155;
}
.ds-option-letter {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 12px;
}

.ds-expand-btn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #ede9fe;
  color: #7c3aed;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}
.ds-expand-btn:hover {
  background: #ddd6fe;
}

.ds-text-clamped {
  font-size: 18px;
  color: #1e293b;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0;
}

/* Option Colors */
.letter-0 {
  background: #3b82f6;
}
.letter-1 {
  background: #ef4444;
}
.letter-2 {
  background: #eab308;
}
.letter-3 {
  background: #10b981;
}

.ds-normal-image {
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 200px;
}
.ds-normal-image img {
  max-width: 100%;
  max-height: 100%;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.canvas-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #64748b;
  gap: 12px;
  font-weight: bold;
  letter-spacing: 1px;
}

.canvas-loading i {
  font-size: 32px;
}

.ds-canvas-watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #cbd5e1;
  font-size: 48px;
  font-weight: 800;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  opacity: 0.5;
  pointer-events: none;
  z-index: 0;
}

.ds-canvas-area iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  border: none;
}

/* Non-CPA Layout */
.ds-non-cpa-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-bottom: 24px;
}
.ds-non-cpa-card {
  flex-direction: column;
  padding: 24px;
  background: white;
  border-radius: 16px;
  margin-bottom: 24px;
}
.ds-normal-image-top {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  max-height: 250px;
}
.ds-normal-image-top img {
  max-width: 100%;
  max-height: 100%;
  cursor: pointer;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
.ds-canvas-non-cpa {
  flex: 1;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  min-height: 400px;
}
</style>

<style>
.el-notification {
  z-index: 1000000005 !important;
}
.el-message {
  z-index: 1000000005 !important;
}
</style>
